<template>
  <div class="task-admin-duplicates-mode">
    <ComponentHeader title="Duplicates Only" subtitle="Identify and manage duplicate opportunities and tasks" />

    <!-- Duplicates Info and Controls -->
    <div class="mb-4 space-y-4 p-4">
      <!-- Filters Panel -->
      <div class="bg-white rounded-lg shadow-sm">
        <h3 class="text-lg font-semibold mb-3">Filters</h3>

        <!-- Metric Filter -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">Metric Type:</label>
          <select
            v-model="selectedMetrics"
            multiple
            @change="applyFilters"
            class="w-full border border-gray-300 rounded-md p-2 max-h-32 overflow-y-auto"
          >
            <option v-for="metric in allMetrics" :key="metric.id" :value="metric.id">
              {{ metric.name }}
            </option>
          </select>
          <p class="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple</p>
        </div>

        <!-- Status Filters -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">Duplicate Status:</label>
          <div class="space-y-2">
            <label class="flex items-center">
              <input
                type="checkbox"
                v-model="statusFilters.primary"
                @change="applyFilters"
                class="mr-2"
              />
              <span class="text-green-600">🟢 Primary ({{ statusCounts.primary }})</span>
            </label>
            <label class="flex items-center">
              <input
                type="checkbox"
                v-model="statusFilters.has_work"
                @change="applyFilters"
                class="mr-2"
              />
              <span class="text-purple-600 font-bold">🟣 Has Work Items - DON'T DELETE ({{ statusCounts.has_work }})</span>
            </label>
            <label class="flex items-center">
              <input
                type="checkbox"
                v-model="statusFilters.has_actions"
                @change="applyFilters"
                class="mr-2"
              />
              <span class="text-yellow-600">🟡 Has Recent Actions ({{ statusCounts.has_actions }})</span>
            </label>
            <label class="flex items-center">
              <input
                type="checkbox"
                v-model="statusFilters.deletable_with_actions"
                @change="applyFilters"
                class="mr-2"
              />
              <span class="text-orange-600">🟠 Deletable - Older Actions ({{ statusCounts.deletable_with_actions }})</span>
            </label>
            <label class="flex items-center">
              <input
                type="checkbox"
                v-model="statusFilters.deletable_clean"
                @change="applyFilters"
                class="mr-2"
              />
              <span class="text-red-600">🔴 Deletable - Clean ({{ statusCounts.deletable_clean }})</span>
            </label>
          </div>
        </div>

        <!-- Quick Filter Buttons -->
        <div class="flex space-x-2">
          <button
            @click="showAll"
            class="px-3 py-1 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors text-sm"
          >
            Show All
          </button>
          <button
            @click="showDeletableOnly"
            class="px-3 py-1 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors text-sm"
          >
            Deletable Only
          </button>
          <button
            @click="showProtectedOnly"
            class="px-3 py-1 bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors text-sm"
          >
            Protected Only
          </button>
        </div>
      </div>

      <!-- Action Buttons and Stats -->
      <div class="flex justify-between items-center">
        <div class="flex items-center space-x-2">
          <button
            v-if="selectedOpportunities.length > 0"
            @click="showBulkDeleteModal = true"
            class="btn-danger"
          >
            <i class="fas fa-trash mr-2"></i>
            Delete Selected ({{ selectedOpportunities.length }})
          </button>

          <button
            @click="loadData"
            class="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
          >
            <i class="fas fa-sync-alt mr-2"></i>Refresh
          </button>
        </div>

        <div class="flex space-x-3">
          <div class="bg-blue-50 rounded-lg px-3 py-2">
            <div class="text-lg font-bold text-blue-600">{{ rowData.length }}</div>
            <div class="text-xs text-gray-600">Total Duplicates</div>
          </div>
          <div class="bg-purple-50 rounded-lg px-3 py-2">
            <div class="text-lg font-bold text-purple-600">{{ statusCounts.has_work }}</div>
            <div class="text-xs text-gray-600">Has Work (Protected)</div>
          </div>
          <div class="bg-green-50 rounded-lg px-3 py-2">
            <div class="text-lg font-bold text-green-600">{{ statusCounts.primary }}</div>
            <div class="text-xs text-gray-600">Primary</div>
          </div>
          <div class="bg-red-50 rounded-lg px-3 py-2">
            <div class="text-lg font-bold text-red-600">{{ deletableCount }}</div>
            <div class="text-xs text-gray-600">Deletable</div>
          </div>
        </div>
      </div>

      <!-- Legend -->
      <div class="bg-gray-50 rounded-lg p-3">
        <div class="text-sm font-medium text-gray-700 mb-2">Legend:</div>
        <div class="grid grid-cols-2 gap-2 text-xs">
          <div class="flex items-center">
            <div class="w-4 h-4 bg-green-100 border border-green-300 rounded mr-1"></div>
            <span>🟢 Primary (Keep)</span>
          </div>
          <div class="flex items-center">
            <div class="w-4 h-4 bg-purple-100 border border-purple-300 rounded mr-1"></div>
            <span class="font-bold">🟣 Has Work Items (DON'T DELETE)</span>
          </div>
          <div class="flex items-center">
            <div class="w-4 h-4 bg-yellow-100 border border-yellow-300 rounded mr-1"></div>
            <span>🟡 Has Recent Actions</span>
          </div>
          <div class="flex items-center">
            <div class="w-4 h-4 bg-orange-100 border border-orange-300 rounded mr-1"></div>
            <span>🟠 Deletable (Older Actions)</span>
          </div>
          <div class="flex items-center">
            <div class="w-4 h-4 bg-red-100 border border-red-300 rounded mr-1"></div>
            <span>🔴 Deletable (Clean)</span>
          </div>
        </div>
      </div>
    </div>

    <!-- AG Grid Table -->
    <div class="h-full header-wrap-table">
      <AgGridVue
        :key="tableKey"
        :theme="standardListTheme"
        style="height: 900px"
        :column-defs="columnDefs"
        :default-col-def="defaultColDef"
        :row-data="rowData"
        :group-display-type="groupDisplayType"
        :grid-options="gridOptions"
        :detail-row-auto-height="true"
        :loading="loading"
        :row-selection="rowSelection"
        :is-row-selectable="isRowSelectable"
        :get-row-id="getRowIdFunction"
        :row-class-rules="rowClassRules"
        @grid-ready="onGridReady"
        @selection-changed="onSelectionChanged"
      />
    </div>

    <!-- Bulk Delete Modal -->
    <ConfirmationModal
      :is-visible="showBulkDeleteModal"
      :message="bulkDeleteMessage"
      @confirm="performBulkDelete"
      @cancel="showBulkDeleteModal = false"
    />

    <!-- Individual Delete Modals -->
    <ConfirmationModal
      :is-visible="isDeleteTaskModalOpen"
      :message="'Are you sure you want to delete this task?'"
      @confirm="deleteTask"
      @cancel="isDeleteTaskModalOpen = !isDeleteTaskModalOpen"
    />

    <ConfirmationModal
      :is-visible="isRefreshOpportunityModalOpen"
      :message="'Are you sure you want to refresh this opportunity?'"
      @confirm="refreshOpportunity"
      @cancel="isRefreshOpportunityModalOpen = !isRefreshOpportunityModalOpen"
    />
  </div>
</template>

<script>
import axios from 'axios';
import { AgGridVue } from 'ag-grid-vue3';
import { standardListTheme } from '@/utils/common';
import CC_Button_Generic_Function from '@/components/widgetcomponents/AG_Grid_Custom_Cells/CC_Button_Generic_Function.vue';
import ConfirmationModal from '@/components/widgetcomponents/ConfirmationModal.vue';
import CC_Object_JSON_Modal from '@/components/widgetcomponents/AG_Grid_Custom_Cells/CC_Object_JSON_Modal.vue';
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';

export default {
  name: 'TaskAdminDuplicatesMode',
  components: {
    AgGridVue,
    ConfirmationModal,
    ComponentHeader
  },
  data() {
    return {
      // Data
      rowData: [],
      allMetrics: [],

      // Filters
      selectedMetrics: [],
      statusFilters: {
        primary: true,
        has_work: true,
        has_actions: true,
        deletable_with_actions: true,
        deletable_clean: true
      },

      // Selection and modals
      selectedOpportunities: [],
      showBulkDeleteModal: false,
      isDeleteTaskModalOpen: false,
      delete_task_id: null,
      isRefreshOpportunityModalOpen: false,
      refreshOpportunityId: null,

      // Grid configuration
      loading: false,
      tableKey: 0,
      gridApi: null,
      groupDisplayType: 'singleColumn',
      rowSelection: {
        mode: 'multiRow',
        checkboxes: true,
        enableClickSelection: false,
        headerCheckbox: true,
      },
      getRowIdFunction: (params) => String(params.data.opportunity_id),
      defaultColDef: {
        filter: true,
        sortable: true,
        resizable: true,
        wrapText: true,
        autoHeight: true,
        cellClass: 'cell-theme-left-aligned',
      },
      gridOptions: {
        masterDetail: true,
        detailCellRendererParams: {
          detailGridOptions: {
            masterDetail: true,
            getRowId: (params) => params.data.task_id,
            detailCellRendererParams: {
              detailGridOptions: {
                getRowId: (params) => params.data.action_id,
                columnDefs: [
                  { headerName: 'Action Type', field: 'action_type_name' },
                  { headerName: 'Action User', field: 'action_user_name' },
                  { headerName: 'Action Date', field: 'action_created_at' }
                ]
              },
              getDetailRowData: (params) => {
                params.successCallback(params.data.actions || []);
              },
            },
            columnDefs: [
              { field: 'task_id', cellRenderer: 'agGroupCellRenderer' },
              { field: 'task_type_name' },
              { field: 'task_order', sort: 'asc', hide: true },
              { field: 'valid_from' },
              { field: 'valid_thru' },
              { field: 'status_name' },
              { cellRenderer: CC_Object_JSON_Modal, field: 'task_metadata', headerName: 'Task Metadata' },
              {
                headerName: 'Delete Task',
                cellClass: 'text-center',
                cellRenderer: CC_Button_Generic_Function,
                cellRendererParams: {
                  function_type: 'delete_v2',
                  openDeleteConfirmationModal: (task_data) => {
                    this.delete_task_id = task_data.task_id;
                    this.isDeleteTaskModalOpen = true;
                  }
                }
              }
            ]
          },
          getDetailRowData: (params) => {
            params.successCallback(params.data.tasks || []);
          }
        },
        suppressAggFuncInHeader: true,
        domLayout: null
      }
    };
  },
  computed: {
    standardListTheme() {
      return standardListTheme;
    },
    statusCounts() {
      const counts = {
        primary: 0,
        has_work: 0,
        has_actions: 0,
        deletable_with_actions: 0,
        deletable_clean: 0
      };
      this.rowData.forEach(row => {
        if (counts.hasOwnProperty(row.duplicate_status)) {
          counts[row.duplicate_status]++;
        }
      });
      return counts;
    },
    deletableCount() {
      return this.rowData.filter(d =>
        d.duplicate_status === 'deletable_clean' ||
        d.duplicate_status === 'deletable_with_actions'
      ).length;
    },
    metricOptions() {
      return this.allMetrics.map(m => ({ value: m.id, label: m.name }));
    },
    bulkDeleteMessage() {
      const cleanDeletable = this.selectedOpportunities.filter(
        opp => opp.duplicate_status === 'deletable_clean'
      ).length;
      const deletableWithActions = this.selectedOpportunities.filter(
        opp => opp.duplicate_status === 'deletable_with_actions'
      ).length;
      const totalDeletable = cleanDeletable + deletableWithActions;

      // Count how many with work were excluded
      const totalWithWork = this.rowData.filter(
        row => (row.duplicate_status === 'deletable_clean' ||
                row.duplicate_status === 'deletable_with_actions') &&
               row.work_count > 0
      ).length;

      let message = `You have selected ${this.selectedOpportunities.length} opportunities for deletion:\n\n`;
      if (cleanDeletable > 0) {
        message += `✓ ${cleanDeletable} have no actions or work\n`;
      }
      if (deletableWithActions > 0) {
        message += `✓ ${deletableWithActions} have older actions only\n`;
      }
      if (totalWithWork > 0) {
        message += `\n⚠️ Note: ${totalWithWork} opportunities with work items were automatically excluded\n`;
      }
      message += `\nAre you sure you want to proceed?`;
      return message;
    },
    rowClassRules() {
      return {
        'bg-red-50': params => params.data && params.data.duplicate_status === 'deletable_clean',
        'bg-orange-50': params => params.data && params.data.duplicate_status === 'deletable_with_actions',
        'bg-yellow-50': params => params.data && params.data.duplicate_status === 'has_actions',
        'bg-green-50': params => params.data && params.data.duplicate_status === 'primary',
        'bg-purple-50': params => params.data && params.data.duplicate_status === 'has_work',
      };
    },
    columnDefs() {
      return [
        { field: 'opportunity_id', headerName: 'ID', cellRenderer: 'agGroupCellRenderer', width: 120 },
        {
          headerName: 'Status',
          field: 'duplicate_status',
          width: 240,
          cellRenderer: params => {
            if (!params.data) return '';
            const status = params.value;
            let displayText = status;
            let className = '';
            let icon = '';

            if (status === 'deletable_clean') {
              displayText = 'Deletable (Clean)';
              className = 'text-red-600 font-medium';
              icon = '🔴';
            } else if (status === 'deletable_with_actions') {
              displayText = 'Deletable (Older Actions)';
              className = 'text-orange-600 font-medium';
              icon = '🟠';
            } else if (status === 'primary') {
              displayText = 'Primary';
              className = 'text-green-600 font-medium';
              icon = '🟢';
            } else if (status === 'has_actions') {
              displayText = 'Has Recent Actions';
              className = 'text-yellow-600 font-medium';
              icon = '🟡';
            } else if (status === 'has_work') {
              displayText = 'Has Work Items (DON\'T DELETE)';
              className = 'text-purple-600 font-bold';
              icon = '🟣';
            }
            return `<span class="${className}">${icon} ${displayText}</span>`;
          }
        },
        { headerName: 'Metric Name', field: 'metric_name', filter: 'agSetColumnFilter', maxWidth: 150 },
        { headerName: 'Practice Name', field: 'practice_name', filter: 'agSetColumnFilter', cellClass: 'sensitive' },
        { field: 'patient_identifier', headerName: 'Patient ID', maxWidth: 150, filter: 'agTextColumnFilter', cellClass: 'sensitive' },
        { headerName: 'Claim Date', field: 'opportunity_metadata.clm_thru_dt', filter: 'agDateColumnFilter', maxWidth: 150 },
        { headerName: 'Dup Count', field: 'duplicate_count', width: 100, cellClass: 'text-center' },
        { field: 'valid_from', filter: 'agDateColumnFilter' },
        { field: 'valid_thru', filter: 'agDateColumnFilter' },
        {
          headerName: 'Work Items',
          field: 'work_count',
          width: 100,
          cellClass: 'text-center',
          cellRenderer: params => {
            if (!params.data) return '';
            const count = params.value || 0;
            if (count > 0) {
              return `<span class="font-bold text-purple-600">${count}</span>`;
            }
            return count;
          }
        },
        {
          headerName: 'Work Types',
          width: 200,
          valueGetter: params => {
            if (!params.data || !params.data.work_items) return '';
            const workTypes = [...new Set(params.data.work_items.map(w => w.work_type_name))];
            return workTypes.join(', ');
          }
        },
        { headerName: 'Actions', field: 'total_action_count', width: 80, cellClass: 'text-center' },
        { headerName: 'Last Action', field: 'last_action_date', width: 150, filter: 'agDateColumnFilter' },
        { headerName: 'Last Work', field: 'last_work_date', width: 150, filter: 'agDateColumnFilter' },
        { cellRenderer: CC_Object_JSON_Modal, field: 'opportunity_metadata', headerName: 'Metadata', width: 100 },
        {
          headerName: 'Refresh',
          cellClass: 'text-center',
          width: 80,
          cellRenderer: CC_Button_Generic_Function,
          cellRendererParams: {
            function_type: 'refresh_opportunity',
            openRefreshOpportunityModal: (opportunity_id) => {
              this.refreshOpportunityId = opportunity_id;
              this.isRefreshOpportunityModalOpen = true;
            }
          }
        }
      ];
    }
  },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        // Build query params
        const params = {};

        // Add metric filter
        if (this.selectedMetrics.length > 0) {
          params.metric_id = this.selectedMetrics.join(',');
        }

        // Add status filter
        const activeStatuses = Object.keys(this.statusFilters).filter(key => this.statusFilters[key]);
        if (activeStatuses.length > 0 && activeStatuses.length < 5) {
          params.duplicate_status = activeStatuses.join(',');
        }

        const response = await axios.get('api/opportunities/duplicates-analysis/', { params });
        this.rowData = response.data?.rowData || [];

        // Extract unique metrics for filter dropdown
        const metricMap = new Map();
        this.rowData.forEach(row => {
          if (!metricMap.has(row.metric_id)) {
            metricMap.set(row.metric_id, { id: row.metric_id, name: row.metric_name });
          }
        });
        this.allMetrics = Array.from(metricMap.values()).sort((a, b) => a.name.localeCompare(b.name));
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        this.loading = false;
      }
    },

    applyFilters() {
      this.loadData();
    },

    showAll() {
      this.statusFilters = {
        primary: true,
        has_work: true,
        has_actions: true,
        deletable_with_actions: true,
        deletable_clean: true
      };
      this.loadData();
    },

    showDeletableOnly() {
      this.statusFilters = {
        primary: false,
        has_work: false,
        has_actions: false,
        deletable_with_actions: true,
        deletable_clean: true
      };
      this.loadData();
    },

    showProtectedOnly() {
      this.statusFilters = {
        primary: true,
        has_work: true,
        has_actions: true,
        deletable_with_actions: false,
        deletable_clean: false
      };
      this.loadData();
    },

    isRowSelectable(params) {
      // Can only select if:
      // 1. Status is deletable (clean or with actions)
      // 2. Does NOT have work items
      return params.data &&
             (params.data.duplicate_status === 'deletable_clean' ||
              params.data.duplicate_status === 'deletable_with_actions') &&
             params.data.work_count === 0;
    },

    onSelectionChanged() {
      if (this.gridApi) {
        this.selectedOpportunities = this.gridApi.getSelectedRows();
      }
    },

    onGridReady(params) {
      this.gridApi = params.api;
      params.api.sizeColumnsToFit();
      window.addEventListener('resize', this.onWindowResize);
    },

    onWindowResize() {
      if (this.gridApi) {
        this.gridApi.sizeColumnsToFit();
      }
    },

    async deleteTask() {
      try {
        await axios.delete(`api/tasks/${this.delete_task_id}/`);
        await this.loadData();
        this.isDeleteTaskModalOpen = false;
        this.delete_task_id = null;
      } catch (error) {
        console.error('Error deleting task', error);
      }
    },

    async refreshOpportunity() {
      try {
        await axios.post(`api/opportunities/${this.refreshOpportunityId}/reconcile/`);
        await this.loadData();
        this.isRefreshOpportunityModalOpen = false;
        this.refreshOpportunityId = null;
      } catch (error) {
        console.error('Error refreshing opportunity', error);
      }
    },

    async performBulkDelete() {
      const opportunityIds = this.selectedOpportunities.map(opp => opp.opportunity_id);
      try {
        await axios.post('api/opportunities/bulk-delete/', { opportunity_ids: opportunityIds });
        this.showBulkDeleteModal = false;
        this.selectedOpportunities = [];
        this.loadData();
      } catch (error) {
        console.error('Error performing bulk delete:', error);
      }
    }
  },
  async mounted() {
    await this.loadData();
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onWindowResize);
  }
};
</script>
